import { Controller, Control, FieldPath, FieldErrors } from 'react-hook-form';
import { Box, IconButton, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import UtmsButton from '@shared/components/ui/UtmsButton';
import ModalDelete from '@shared/components/modals/ModalDelete';
import { useMemo, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import { AutoLinkPatternField } from '../types';

interface DefectAutoLinkPatternFieldsProps<T extends Record<string, any>> {
    basePath: FieldPath<T>;
    control: Control<T>;
    errors?: FieldErrors<T>;
    onViewMatch?: (fieldName: AutoLinkPatternField) => void;
    onRemove?: () => void;
}

const DefectAutoLinkPatternFields = <T extends Record<string, any>>({
    basePath,
    control,
    errors,
    onViewMatch,
    onRemove,
}: DefectAutoLinkPatternFieldsProps<T>) => {
    const { t } = useTranslation();
    const fieldErrors = basePath.split('.').reduce((acc, key) => acc?.[key], errors as any);
    const [openDeleteAutoLinkPatternModal, setOpenDeleteAutoLinkPatternModal] = useState(false);
    const fields = useMemo(
        () => [
            { name: 'name', label: t('rule_name') + '*', action: 'remove' as const },
            { name: 'errorMessagePattern', label: t('error_message_pattern'), action: 'match' as const },
            { name: 'stackTracePattern', label: t('stack_trace_pattern'), action: 'match' as const },
        ],
        [t],
    );

    const handleDeleteAutoLinkPatternItemSubmit = () => {
        setOpenDeleteAutoLinkPatternModal(false);
        onRemove?.();
    };

    const handleOpenDeleteAutoLinkPatternModal = () => {
        setOpenDeleteAutoLinkPatternModal(true);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, gap: 2 }}>
            {fields.map(({ name, label, action }) => (
                <Box key={name} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Controller
                        name={`${basePath}.${name}` as any}
                        control={control}
                        rules={{ required: t('field_required') }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                size="small"
                                label={label}
                                fullWidth
                                error={Boolean(fieldErrors?.[name])}
                                helperText={fieldErrors?.[name]?.message}
                            />
                        )}
                    />
                    {action === 'remove' && onRemove && (
                        <IconButton color="error" onClick={handleOpenDeleteAutoLinkPatternModal}>
                            <DeleteIcon />
                        </IconButton>
                    )}
                    {action === 'match' && onViewMatch && (
                        <UtmsButton
                            onClick={() => onViewMatch(name as AutoLinkPatternField)}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            {t('view_matched_results')}
                        </UtmsButton>
                    )}
                </Box>
            ))}
            <ModalDelete
                open={openDeleteAutoLinkPatternModal}
                setOpen={setOpenDeleteAutoLinkPatternModal}
                handleSubmit={handleDeleteAutoLinkPatternItemSubmit}
                objectName={t('automation_rule').toLowerCase()}
            />
        </Box>
    );
};
export default DefectAutoLinkPatternFields;
